import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Visibility } from '@mui/icons-material';
import PageMeta from '../../components/PageMeta';
import useDebounce from '../../hooks/useDebounce';
import CustomPagination from '../../components/common/CustomPagination';
import { getDeviceInfo, getDeviceInfos } from '../../services/deviceInfo';

const formatDateTime = (value) => {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }

  return parsed.toLocaleString();
};

const DeviceInfo = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    platform_type: '',
    browser_name: '',
    os_name: '',
  });
  const [detailsModal, setDetailsModal] = useState({ open: false, loading: false, device: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const debouncedSearch = useDebounce(filters.search, 400);
  const debouncedBrowser = useDebounce(filters.browser_name, 400);
  const debouncedOs = useDebounce(filters.os_name, 400);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDeviceInfos({
        page: page + 1,
        page_size: rowsPerPage,
        search: debouncedSearch,
        platform_type: filters.platform_type,
        browser_name: debouncedBrowser,
        os_name: debouncedOs,
      });
      setRows(response.results || []);
      setRowCount(Number(response.total || 0));
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [debouncedBrowser, debouncedOs, debouncedSearch, filters.platform_type, page, rowsPerPage]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));

  const handleFilterChange = (field, value) => {
    setPage(0);
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleRefresh = () => {
    loadDevices();
  };

  const handleClearFilters = () => {
    setPage(0);
    setFilters({
      search: '',
      platform_type: '',
      browser_name: '',
      os_name: '',
    });
  };

  const handleViewDetails = async (deviceId) => {
    setDetailsModal({ open: true, loading: true, device: null });
    try {
      const device = await getDeviceInfo(deviceId);
      setDetailsModal({ open: true, loading: false, device });
    } catch (error) {
      setDetailsModal({ open: false, loading: false, device: null });
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
      return;
    }

    setRowsPerPage(Number(value));
    setPage(0);
    setCustomRowsActive(false);
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const columns = useMemo(() => ([
    {
      field: 'username',
      headerName: 'User',
      minWidth: 180,
      flex: 1,
      valueGetter: (_value, row) => row.user?.fullname || row.user?.username || 'Unknown User',
      renderCell: ({ row }) => (
        <Box sx={{ py: 1 }}>
          <Typography >
            {row.user?.email || row.user?.phone || row.user?.username || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'platform_type',
      headerName: 'Access',
      width: 110,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={(row.platform_type || 'unknown').toUpperCase()}
          color={row.platform_type === 'admin' ? 'secondary' : row.platform_type === 'web' ? 'primary' : 'success'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'display_name',
      headerName: 'Device',
      minWidth: 220,
      flex: 1.2,
      renderCell: ({ row }) => (
        <Box sx={{ py: 1 }}>

          <Typography  >
            {[row.device_brand, row.device_model].filter(Boolean).join(' • ') || row.device_id || 'No device id'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'os_name',
      headerName: 'OS',
      minWidth: 150,
      flex: 0.8,
      valueGetter: (_value, row) => [row.os_name, row.os_version].filter(Boolean).join(' '),
    },
    {
      field: 'browser_name',
      headerName: 'Browser',
      minWidth: 160,
      flex: 0.9,
      valueGetter: (_value, row) => [row.browser_name, row.browser_version].filter(Boolean).join(' '),
    },
    {
      field: 'language',
      headerName: 'Language',
      minWidth: 110,
      flex: 0.7,
    },
    {
      field: 'timezone',
      headerName: 'Timezone',
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'last_seen_at',
      headerName: 'Last Seen',
      minWidth: 190,
      flex: 1,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: 'last_login_at',
      headerName: 'Last Login',
      minWidth: 190,
      flex: 1,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <IconButton color="primary" onClick={() => handleViewDetails(row.id)}>
          <Visibility />
        </IconButton>
      ),
    },
  ]), []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <PageMeta title="Device Diagnostics" description="Admin device diagnostics management" />

      <Grid container spacing={{ xs: 1.5, md: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2,
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 2, sm: 2.25 },
              boxShadow: theme.shadows[2],
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Device Info Management
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  Search user devices, browsers, and recent access activity.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{
                    color: 'primary.contrastText',
                    borderColor: 'rgba(255,255,255,0.45)',
                    '&:hover': {
                      borderColor: 'primary.contrastText',
                      backgroundColor: 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  sx={{
                    backgroundColor: 'common.white',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                  }}
                >
                  Refresh
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent>
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                <Grid size={{ xs: 12, md: 4.5 }}>
                  <TextField
                    label="Search user or device"
                    value={filters.search}
                    onChange={(event) => handleFilterChange('search', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                  <TextField
                    select
                    label="Access Type"
                    value={filters.platform_type}
                    onChange={(event) => handleFilterChange('platform_type', event.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="app">App</MenuItem>
                    <MenuItem value="web">Web</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                  <TextField
                    label="Browser"
                    value={filters.browser_name}
                    onChange={(event) => handleFilterChange('browser_name', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                  <TextField
                    label="OS"
                    value={filters.os_name}
                    onChange={(event) => handleFilterChange('os_name', event.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  minHeight: 640,
                  '&::-webkit-scrollbar': { height: 8 },
                  '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 4 },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 4 },
                  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
                }}
              >
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  rowCount={rowCount}
                  pageSize={rowsPerPage}
                  rowsPerPageOptions={[]}
                  hideFooter
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    minWidth: 1180,
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: 'primary.main',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: 'common.white',
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                      color: 'common.white',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#FFF0F3',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #f0f0f0',
                      alignItems: 'center',
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <CustomPagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            customRowsActive={customRowsActive}
            customRowsInput={customRowsInput}
            onCustomRowsInputChange={handleCustomRowsInputChange}
            onCustomRowsInputBlur={handleCustomRowsInputBlur}
          />
        </Grid>
      </Grid>

      <Dialog
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, loading: false, device: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Device Diagnostics Details</DialogTitle>
        <DialogContent dividers>
          {detailsModal.loading ? (
            <Typography>Loading device details...</Typography>
          ) : detailsModal.device ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">User</Typography>
                <Typography variant="body1">
                  {detailsModal.device.user?.fullname || detailsModal.device.user?.username || 'Unknown User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {detailsModal.device.user?.email || detailsModal.device.user?.phone || 'N/A'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Device Summary</Typography>
                <Typography variant="body2">Access Type: {detailsModal.device.platform_type || 'N/A'}</Typography>
                <Typography variant="body2">Device: {detailsModal.device.display_name || 'N/A'}</Typography>
                <Typography variant="body2">Model: {detailsModal.device.device_model || 'N/A'}</Typography>
                <Typography variant="body2">Brand: {detailsModal.device.device_brand || 'N/A'}</Typography>
                <Typography variant="body2">Device ID: {detailsModal.device.device_id || 'N/A'}</Typography>
                <Typography variant="body2">Platform: {detailsModal.device.platform || 'N/A'}</Typography>
                <Typography variant="body2">OS: {[detailsModal.device.os_name, detailsModal.device.os_version].filter(Boolean).join(' ') || 'N/A'}</Typography>
                <Typography variant="body2">Browser: {[detailsModal.device.browser_name, detailsModal.device.browser_version].filter(Boolean).join(' ') || 'N/A'}</Typography>
                <Typography variant="body2">Language: {detailsModal.device.language || 'N/A'}</Typography>
                <Typography variant="body2">Timezone: {detailsModal.device.timezone || 'N/A'}</Typography>
                <Typography variant="body2">App Version: {detailsModal.device.app_version || 'N/A'}</Typography>
                <Typography variant="body2">Build Number: {detailsModal.device.build_number || 'N/A'}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Activity</Typography>
                <Typography variant="body2">Location: {detailsModal.device.location || 'Unknown Location'}</Typography>
                <Typography variant="body2">IP Address: {detailsModal.device.ip_address || 'N/A'}</Typography>
                <Typography variant="body2">Last Request Path: {detailsModal.device.last_request_path || 'N/A'}</Typography>
                <Typography variant="body2">First Seen: {formatDateTime(detailsModal.device.first_seen_at)}</Typography>
                <Typography variant="body2">Last Seen: {formatDateTime(detailsModal.device.last_seen_at)}</Typography>
                <Typography variant="body2">Last Login: {formatDateTime(detailsModal.device.last_login_at)}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">User Agent</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {detailsModal.device.user_agent || 'N/A'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Metadata</Typography>
                <Box
                  component="pre"
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: '#f8f9fa',
                    overflowX: 'auto',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(detailsModal.device.metadata || {}, null, 2)}
                </Box>
              </Box>
            </Stack>
          ) : (
            <Typography>No details available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceInfo;
